import { forwardRef } from 'react';
import type { PetProfileCardPayload, WidgetProps } from '@chewy/chat-library-common';
import { WidgetContainer, ActionButton, AddPetCard } from '@chewy/chat-library-components';
import first from 'lodash/first';
import styles from './PetProfileCard.module.scss';
import { getPetImageUrl } from './petImages';

function getPetAge(birthday: string): string {
  if (!birthday) {
    return '';
  }
  const birthDate = new Date(birthday);
  if (isNaN(birthDate.getTime())) return '';
  const today = new Date();

  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();

  // Adjust if the current day is before the birth day in the month
  if (days < 0) {
    months--;
    // Get the number of days in the previous month
    const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
    days += prevMonth.getDate();
  }

  // Adjust years and months if months is negative
  if (months < 0) {
    years--;
    months += 12;
  }

  // Less than 1 month old - show days
  if (years < 1 && months < 1) {
    return `${days} days old`;
  }

  // Less than 1 year old - show months
  if (years < 1) {
    return `${months} mo old`;
  }

  return `${years} yr old`;
}

function getDisplayBreedName(breedName: string): string {
  return breedName.trim().toUpperCase() === 'UNKNOWN' ? '' : breedName;
}

const PetProfileCard = forwardRef<HTMLDivElement, WidgetProps<PetProfileCardPayload>>(function PetProfileCard(
  { data, isSelectable = false, isSelected, isUnselected, onSelect },
  ref
) {
  const petData = first(data) || {};

  const {
    id = '',
    name = '',
    breedName = '',
    weightInPounds = '',
    birthday = '',
    imageUrls = [],
    breedType = '',
    actions = []
  } = petData;
  const hasActions = actions.length > 0;
  if (id === '' && hasActions) {
    return <AddPetCard action={actions[0]} widgetData={petData} />;
  }

  const avatarUrl = getPetImageUrl(imageUrls[0], breedType);
  const displayBreedName = getDisplayBreedName(breedName);
  const petAge = getPetAge(birthday);
  const petWeight = weightInPounds ? `${weightInPounds} lbs` : '';
  const petDetailsText = [displayBreedName, petAge, petWeight].filter(Boolean).join(' • ');

  return (
    <WidgetContainer
      isSelectable={isSelectable}
      isSelected={isSelected}
      isUnselected={isUnselected}
      onSelect={onSelect}
      hasActions={hasActions}
      data={petData}
    >
      <div ref={ref} className={styles.container}>
        <div className={styles.content}>
          <div className={styles.avatar}>
            {avatarUrl && <img src={avatarUrl} alt={`${name}'s avatar`} />}
          </div>
          <div className={styles.details}>
            <span className={styles.name}>{name}</span>
            {petDetailsText && (
              <div className={styles.info}>
                <p aria-label={petDetailsText}>
                  {displayBreedName && (
                    <span>
                      {displayBreedName}
                      {(petAge || petWeight) && <span className={styles.divider}>•</span>}
                    </span>
                  )}
                  {petAge && (
                    <span className={styles.noWrapDetail}>
                      {petAge}
                      {petWeight && <span className={styles.divider}>•</span>}
                    </span>
                  )}
                  {petWeight && <span className={styles.noWrapDetail}>{petWeight}</span>}
                </p>
              </div>
            )}
          </div>
        </div>
        {hasActions && (
          <div>
            <div className={styles.actions}>
              {actions.map((action, index) => (
                <ActionButton key={index} widgetData={petData} action={action} />
              ))}
            </div>
          </div>
        )}
      </div>
    </WidgetContainer>
  );
});

export default PetProfileCard;
